import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Collection", to: "/shop", type: "route" },
  { label: "Décor", hash: "#decorations", type: "hash" },
  { label: "Story", hash: "#story", type: "hash" },
  { label: "Gallery", hash: "#gallery", type: "hash" },
  { label: "Contact", hash: "#contact", type: "hash" },
];

export default function Navbar() {
  const { count, setIsOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const goHash = (hash) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(hash), 500);
    } else {
      scrollToId(hash);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-forest/10 bg-cream/75 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <img
            src="/images/logo.png"
            alt="Utsav Flowers logo"
            data-testid="nav-logo-image"
            className="h-11 w-auto"
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">
            Flowers · Est 1999
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-xs font-bold uppercase tracking-[0.2em] text-forest/70 transition-colors duration-300 hover:text-forest"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.label}
                onClick={() => goHash(l.hash)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-xs font-bold uppercase tracking-[0.2em] text-forest/70 transition-colors duration-300 hover:text-forest"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cart-button"
            onClick={() => setIsOpen(true)}
            className="relative flex h-11 w-11 items-center justify-center rounded-full border border-forest/20 transition-colors duration-300 hover:bg-forest hover:text-cream"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-marigold text-[10px] font-bold text-forest"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-forest/20 lg:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-forest/10 bg-cream px-6 py-6 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-serif text-2xl italic"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  onClick={() => goHash(l.hash)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="text-left font-serif text-2xl italic"
                >
                  {l.label}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
